import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useRouter } from 'next/router'

import type { Session } from 'next-auth'

// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type Braze from '@braze/web-sdk'

type BrazeContextType = {
	brazeFunction: () => Promise<typeof Braze | undefined>
	getBrazeExternalId: (session: Session | null) => string
}

// Braze Context
const getBrazeExternalId = (session: Session | null) => {
	// Use Shopify Customer ID when logged in
	if (session?.user?.customer?.id) {
		// Extract numeric ID from GraphQL Global ID (e.g., "gid://shopify/Customer/6779975336073" -> "6779975336073")
		const customerId = session.user.customer.id
		const numericId = customerId.includes('gid://shopify/Customer/') 
			? customerId.split('/').pop() 
			: customerId
		return numericId
	}
	
	// For anonymous users, use email if available
	if (session?.user?.email) {
		return session.user.email
	}
	
	return null
}

export const BrazeContext = createContext<BrazeContextType>({
	brazeFunction: async () => undefined,
	getBrazeExternalId
})

// Use braze function
export default function useBrazeIntegration() {
	const { brazeFunction } = useContext(BrazeContext)
	return { brazeFunction, getBrazeExternalId }
}

// Braze Provider
export function BrazeProvider({ children }: { children: ReactNode }) {
	const [brazeImport, setBrazeImport] = useState<typeof Braze>()
	const router = useRouter()

	const brazeFunction = useCallback(async () => {
		let braze: any = brazeImport

		if (!brazeImport) {
			// check if braze is already initialized
			if (braze) {
				return braze
			}

			braze = await import('@braze/web-sdk')

			if (process.env.NEXT_PUBLIC_BRAZE_TOKEN_CLIENT) {
				braze.initialize(process.env.NEXT_PUBLIC_BRAZE_TOKEN_CLIENT, {
					baseUrl: 'sdk.iad-05.braze.com',
					enableSdkAuthentication: true,
					allowUserSuppliedJavascript: true,
					enableLogging: false,

				})
				// Only enable in-app messages if not on signature box page
				braze.automaticallyShowInAppMessages()
			}

			setBrazeImport(braze)
		}

		return braze
	}, [brazeImport])

	// Memoize context value
	const contextValue = useMemo(() => ({ brazeFunction, getBrazeExternalId }), [brazeFunction])

	return <BrazeContext.Provider value={contextValue}>{children}</BrazeContext.Provider>
}
